import { BetupFeedChannelPostDto } from './BetupFeedChannelDto';

/** BETUP Feed › Publishing (betup-server-api docs/channel-articles.md §8–9): AI channel articles and channel posts. */

export interface PublishingMatchDto {
    id: number;
    sport_id: number | null;
    league: string | null;
    home: string | null;
    away: string | null;
    start: string | null;
}

/** A channel post as Publishing lists it: the channel post fields (its card and editor work as they are) plus more. */
export interface PublishingPostDto extends BetupFeedChannelPostDto {
    channel_id: number;
    channel_name: string | null;
    linked_match: PublishingMatchDto | null;
    /** The AI article invocation that wrote it; null = written by a person. */
    ai_execution_id: number | null;
    occurred_at: string;
}

export interface PublishingPostPageDto {
    items: PublishingPostDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

export type PublishingPostStatus = 'draft' | 'scheduled' | 'published' | 'all';

/** An article invocation's stage, in order; COMPLETED and FAILED end it. */
export type ChannelArticleStatus =
    'NOT_STARTED' | 'ASSIGNED' | 'COLLECTING_CONTEXT' | 'WEB_RESEARCH' | 'WRITING' | 'VALIDATING' | 'TRANSLATIONS' | 'SAVING' | 'COMPLETED' | 'FAILED';

export const CHANNEL_ARTICLE_STAGES: ChannelArticleStatus[] =
    ['NOT_STARTED', 'ASSIGNED', 'COLLECTING_CONTEXT', 'WEB_RESEARCH', 'WRITING', 'VALIDATING', 'TRANSLATIONS', 'SAVING', 'COMPLETED', 'FAILED'];

/** One AI channel article invocation (qbet_channel_article_execution). */
export interface ChannelArticleExecutionDto {
    id: number;
    source: 'SCHEDULED' | 'MANUAL' | string;
    requested_by: string | null;
    requested_channel_id: number | null;
    requested_match_id: number | null;
    channel_id: number | null;
    channel_name: string | null;
    match: PublishingMatchDto | null;
    status: ChannelArticleStatus;
    failed_stage: ChannelArticleStatus | null;
    error: string | null;
    note: string | null;
    /** The AI hub workflow run key of all its calls (workflow channel.article). */
    correlation_id: string | null;
    research_id: number | null;
    research_run_id: string | null;
    research_reused: boolean;
    researched_at: string | null;
    post_id: number | null;
    auto_published: boolean;
    cost_usd: number | null;
    stages: { stage: ChannelArticleStatus; at: string }[] | null;
    created_at: string;
    updated_at: string;
    finished_at: string | null;
}

export interface ChannelArticleExecutionPageDto {
    items: ChannelArticleExecutionDto[];
    offset: number;
    limit: number;
    totalItems: number;
    failures_24h: number;
    max_failures_24h: number;
    running: number;
}

/** A match's channel posts (drafts too) and an article invocation in progress. */
export interface MatchPublishingDto {
    match_id: number;
    posts: PublishingPostDto[];
    running: ChannelArticleExecutionDto | null;
}

/** The `channel_article.*` app settings (they limit the Crons scheduler only; auto-publish applies to every article). */
export const CHANNEL_ARTICLE_SETTING_KEYS = {
    enabled: 'channel_article.enabled',
    channelIds: 'channel_article.channel_ids',
    windowHours: 'channel_article.window_hours',
    minBets: 'channel_article.min_bets',
    intervalMinutes: 'channel_article.interval_minutes',
    maxArticlesPerRun: 'channel_article.max_articles_per_run',
    maxPerChannel24h: 'channel_article.max_per_channel_24h',
    maxFailures24h: 'channel_article.max_failures_24h',
    autoPublish: 'channel_article.auto_publish',
} as const;

/** The hub workflow AI channel articles run in. */
export const CHANNEL_ARTICLE_WORKFLOW = 'channel.article';
